import { OktaContext } from '@okta/okta-react';
import {
  useLocationHash,
  useLocationPathname,
  useLocationSearch,
  useLocationState,
  Navigate,
  RouteProps,
} from '@rocketlane/rocket-router';
import React, { useContext } from 'react';
import { PageLoading } from 'components/atoms/loading';
import { RoutePaths } from 'constant';
// import { useFieldsLoaded } from 'hooks/use-fields-loaded';
import { useCustomAuth } from 'hooks/use-custom-auth';

type RoutePropsWithLoading = RouteProps & { isLoading?: boolean };

const StableRouteChildren = React.memo(
  function StableRouteChildren({
    children,
  }: {
    children: React.ReactNode;
  }): React.ReactElement | React.ReactNode {
    return children;
  },
  (prev, next) => prev.children === next.children,
);

StableRouteChildren.displayName = 'StableRouteChildren';

/**
 * Only mounts when redirecting to login. Uses pathname/search/hash/state for Navigate.
 * Isolated so hash changes don't re-render the authenticated route tree.
 */
const RedirectToLogin = ({
  onAuthRequired,
}: {
  onAuthRequired?: () => void;
}) => {
  const oktaContext = useContext(OktaContext);
  const oktaAuth = oktaContext?.oktaAuth;
  const pathname = useLocationPathname();
  const search = useLocationSearch();
  const hash = useLocationHash();
  const state = useLocationState();

  if (onAuthRequired) {
    onAuthRequired();
  } else if (oktaAuth) {
    oktaAuth.signInWithRedirect({
      originalUri: pathname,
    });
  }

  return (
    <Navigate
      to={RoutePaths.login}
      state={{ from: { pathname, search, hash, state } }}
    />
  );
};

/**
 * SecureRoute shell: uses only authState. When authenticated, renders children
 * without subscribing to hash/search/state — so task drawer open/close (hash-only)
 * does NOT cause re-renders.
 */
export const SecureRoute = ({ children, ...rest }) => {
  const oktaContext = useContext(OktaContext);
  const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
  const authState = oktaContext
    ? oktaContext.authState
    : { isAuthenticated: customIsAuthenticated };
  const { onAuthRequired } = rest;

  if (!authState) {
    // eslint-disable-next-line @calm/react-intl/missing-formatted-message
    return <div>Loading...</div>;
  }

  if (!authState.isAuthenticated) {
    return <RedirectToLogin onAuthRequired={onAuthRequired} />;
  }

  return <StableRouteChildren>{children}</StableRouteChildren>;
};

export const CustomSecureRoute = (
  props: RoutePropsWithLoading,
): React.ReactElement => {
  const { isLoading } = props;

  return isLoading ? (
    <PageLoading css />
  ) : (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SecureRoute {...props}>{props?.children}</SecureRoute>
  );
};
